// Métricas de tracking GPS de aplicaciones en campo.
// Toma una lista de puntos AppBoletaCampoTrack ordenados por t y devuelve
// distancia, velocidades, tiempos muertos y cobertura del lote.

import area from '@turf/area';
import buffer from '@turf/buffer';
import intersect from '@turf/intersect';
import { lineString, polygon, featureCollection } from '@turf/helpers';
import type { AppBoletaCampoTrack } from '../../types/empacadora';

// Radio terrestre medio (m)
const R = 6_371_000;

/** Distancia Haversine entre dos puntos en metros. */
export function distanciaMetros(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export interface MetricasRecorrido {
  puntos: number;                 // cantidad de muestras GPS válidas
  distancia_m: number;            // distancia total recorrida (m)
  duracion_s: number;             // duración entre primer y último punto (s)
  velocidad_media_kmh: number;    // distancia/duración
  velocidad_max_kmh: number;      // máximo de segmentos válidos
  tiempos_muertos: { inicio: string; fin: string; segundos: number }[]; // segmentos sin movimiento real
  tiempo_muerto_total_s: number;
}

/**
 * Calcula métricas a partir de una lista de puntos GPS ordenados por timestamp.
 *
 * @param tracks puntos GPS ordenados por t ascendente
 * @param opts.umbral_movimiento_m   distancia mínima en metros para considerar movimiento entre 2 muestras (default 2)
 * @param opts.umbral_muerto_s       segundos mínimos sin movimiento para reportar un tiempo muerto (default 120)
 */
export function calcularMetricas(
  tracks: AppBoletaCampoTrack[],
  opts: { umbral_movimiento_m?: number; umbral_muerto_s?: number } = {},
): MetricasRecorrido {
  const umbralMovimiento = opts.umbral_movimiento_m ?? 2;
  const umbralMuerto     = opts.umbral_muerto_s ?? 120;

  if (tracks.length < 2) {
    return {
      puntos: tracks.length,
      distancia_m: 0,
      duracion_s: 0,
      velocidad_media_kmh: 0,
      velocidad_max_kmh: 0,
      tiempos_muertos: [],
      tiempo_muerto_total_s: 0,
    };
  }

  let distanciaTotal = 0;
  let velMax = 0;
  const tiemposMuertos: { inicio: string; fin: string; segundos: number }[] = [];
  let muertoDesde: string | null = null;
  let muertoSeg = 0;

  for (let i = 1; i < tracks.length; i++) {
    const a = tracks[i - 1];
    const b = tracks[i];
    const dt_s = (new Date(b.t).getTime() - new Date(a.t).getTime()) / 1000;
    if (dt_s <= 0) continue;

    const d_m = distanciaMetros(a, b);

    if (d_m < umbralMovimiento) {
      // segmento sin movimiento real
      if (muertoDesde == null) muertoDesde = a.t;
      muertoSeg += dt_s;
      if (muertoSeg >= umbralMuerto && i === tracks.length - 1) {
        // cerrar al final
        tiemposMuertos.push({ inicio: muertoDesde, fin: b.t, segundos: muertoSeg });
      }
      continue;
    }

    // hubo movimiento — cerrar muerto pendiente si pasaba el umbral
    if (muertoDesde != null && muertoSeg >= umbralMuerto) {
      tiemposMuertos.push({ inicio: muertoDesde, fin: a.t, segundos: muertoSeg });
    }
    muertoDesde = null;
    muertoSeg = 0;

    distanciaTotal += d_m;
    const v_kmh = (d_m / dt_s) * 3.6;
    if (v_kmh > velMax) velMax = v_kmh;
  }

  const duracion_s = (new Date(tracks[tracks.length - 1].t).getTime() - new Date(tracks[0].t).getTime()) / 1000;
  const velocidad_media_kmh = duracion_s > 0 ? (distanciaTotal / duracion_s) * 3.6 : 0;
  const tiempo_muerto_total_s = tiemposMuertos.reduce((s, t) => s + t.segundos, 0);

  return {
    puntos: tracks.length,
    distancia_m: distanciaTotal,
    duracion_s,
    velocidad_media_kmh,
    velocidad_max_kmh: velMax,
    tiempos_muertos: tiemposMuertos,
    tiempo_muerto_total_s,
  };
}

/**
 * Calcula cobertura del lote como porcentaje del área del lote que cae dentro
 * del buffer del recorrido (ancho/2 a cada lado de la polyline).
 *
 * @returns { cobertura_pct, area_lote_m2, area_cubierta_m2 } o null si no se puede calcular
 */
export function calcularCobertura(
  tracks: AppBoletaCampoTrack[],
  loteGeojson: any,
  anchoBoom_m: number,
): { cobertura_pct: number; area_lote_m2: number; area_cubierta_m2: number } | null {
  if (tracks.length < 2 || !loteGeojson || !anchoBoom_m || anchoBoom_m <= 0) return null;

  try {
    // Polyline del recorrido [lng, lat]
    const coords: [number, number][] = tracks.map(t => [Number(t.lng), Number(t.lat)]);
    const linea = lineString(coords);

    // Buffer de la línea = mitad del ancho a cada lado (en km porque @turf/buffer usa km por default)
    const bufferKm = (anchoBoom_m / 2) / 1000;
    const recorridoBuffered: any = buffer(linea, bufferKm, { units: 'kilometers' });
    if (!recorridoBuffered) return null;

    // Normalizar lote a Feature<Polygon> — el geojson puede venir como Polygon, Feature, o FeatureCollection
    const lotePoly = normalizarLoteAPoligono(loteGeojson);
    if (!lotePoly) return null;

    const interseccion = intersect(featureCollection([lotePoly, recorridoBuffered]) as any);
    const area_lote_m2 = area(lotePoly);
    const area_cubierta_m2 = interseccion ? area(interseccion) : 0;
    const cobertura_pct = area_lote_m2 > 0 ? (area_cubierta_m2 / area_lote_m2) * 100 : 0;

    return { cobertura_pct, area_lote_m2, area_cubierta_m2 };
  } catch (err) {

    return null;
  }
}

function normalizarLoteAPoligono(geojson: any): any {
  if (!geojson) return null;
  if (geojson.type === 'Feature' && (geojson.geometry?.type === 'Polygon' || geojson.geometry?.type === 'MultiPolygon')) {
    return geojson;
  }
  if (geojson.type === 'FeatureCollection') {
    const feat = (geojson.features || []).find((f: any) => f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'MultiPolygon');
    return feat ?? null;
  }
  if (geojson.type === 'Polygon') {
    return polygon(geojson.coordinates);
  }
  if (geojson.type === 'MultiPolygon') {
    // tomar primer polígono
    return polygon(geojson.coordinates[0]);
  }
  return null;
}

// Thresholds para coloreo de KPIs en V°B°. Devuelven 'ok' | 'warn' | 'bad'.

export type SeveridadKPI = 'ok' | 'warn' | 'bad';

export function severidadDuracion(min: number): SeveridadKPI {
  if (min < 2) return 'bad';
  if (min < 10) return 'warn';
  return 'ok';
}

export function severidadVelocidadMedia(kmh: number): SeveridadKPI {
  if (kmh < 1) return 'warn';
  if (kmh > 8) return 'warn';
  return 'ok';
}

export function severidadCobertura(pct: number): SeveridadKPI {
  if (pct < 50) return 'bad';
  if (pct < 80) return 'warn';
  return 'ok';
}

export function severidadTiemposMuertos(n: number): SeveridadKPI {
  if (n === 0) return 'ok';
  if (n <= 2) return 'warn';
  return 'bad';
}
